import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X } from "lucide-react";
import { sdgLabel } from "../lib/sdg";
import { RaisePurposePicker } from "./founder/RaisePurposePicker";

const SECTORS = ["Fintech","Agritech","Healthtech","Cleantech","Logistics","E-commerce","Manufacturing","Edtech","Hospitality"];
const COUNTRIES = ["Nigeria","Kenya","Ghana","South Africa","Egypt","Rwanda","Senegal","Ethiopia","Tanzania","Uganda"];
const STAGES = ["Idea","MVP","Revenue","Growth"];
const SDGS = ["SDG 2","SDG 3","SDG 4","SDG 5","SDG 7","SDG 8","SDG 9","SDG 13"];

export default function EditCompanyModal({ open, onClose, onSaved }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/companies/me");
        setC({
          company_name: data.company_name || "", website: data.website || "",
          sector: data.sector || "Fintech", country: data.country || "Nigeria",
          stage: data.stage || "Revenue", ticket_min: data.ticket_min || 250000,
          ticket_max: data.ticket_max || 1000000, value_prop: data.value_prop || "",
          revenue_annual: data.revenue_annual || 0, employees: data.employees || 1,
          sdgs: data.sdgs || [],
          raise_reasons: data.raise_reasons || [],
          raise_reason_other: data.raise_reason_other || "",
          capital_purposes: data.capital_purposes || [],
        });
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.error("[EditCompanyModal] load failed", err);
      }
    })();
  }, [open]);

  if (!open) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/companies/me", c);
      if (c.raise_reasons.length > 0) {
        await api.put("/companies/me/raise-purpose", {
          raise_reasons: c.raise_reasons,
          raise_reason_other: c.raise_reason_other.trim(),
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("[EditCompanyModal] save failed", err);
      window.alert(err?.response?.data?.detail || "Couldn't save changes");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="edit-company-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-neutral-200 bg-white px-6 py-4">
          <h2 className="text-lg font-bold text-neutral-900">Edit company profile</h2>
          <button onClick={onClose} data-testid="edit-close" className="rounded-lg p-1 hover:bg-neutral-100"><X className="h-4 w-4"/></button>
        </div>
        {!c ? <p className="p-8 text-sm text-neutral-500">Loading…</p> :
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <F label="Company name"><input data-testid="edit-name" value={c.company_name} onChange={e=>setC({...c,company_name:e.target.value})} className={inp}/></F>
              <F label="Website"><input data-testid="edit-website" value={c.website} onChange={e=>setC({...c,website:e.target.value})} className={inp}/></F>
              <F label="Sector"><select value={c.sector} onChange={e=>setC({...c,sector:e.target.value})} className={inp}>{SECTORS.map(s=><option key={s}>{s}</option>)}</select></F>
              <F label="Country"><select value={c.country} onChange={e=>setC({...c,country:e.target.value})} className={inp}>{COUNTRIES.map(s=><option key={s}>{s}</option>)}</select></F>
              <F label="Stage"><select value={c.stage} onChange={e=>setC({...c,stage:e.target.value})} className={inp}>{STAGES.map(s=><option key={s}>{s}</option>)}</select></F>
              <F label="Revenue (USD)"><input type="number" value={c.revenue_annual} onChange={e=>setC({...c,revenue_annual:+e.target.value})} className={inp}/></F>
              <F label="Ticket min"><input type="number" value={c.ticket_min} onChange={e=>setC({...c,ticket_min:+e.target.value})} className={inp}/></F>
              <F label="Ticket max"><input type="number" value={c.ticket_max} onChange={e=>setC({...c,ticket_max:+e.target.value})} className={inp}/></F>
            </div>
            <F label="Value proposition">
              <textarea data-testid="edit-value-prop" rows={3} value={c.value_prop} onChange={e=>setC({...c,value_prop:e.target.value})} className={inp+" resize-none"}/>
            </F>
            <F label="Impact areas">
              <div className="flex flex-wrap gap-2">
                {SDGS.map(s => {
                  const on = (c.sdgs||[]).includes(s);
                  return <button key={s} type="button" onClick={()=>setC(x=>({...x, sdgs: on?x.sdgs.filter(y=>y!==s):[...(x.sdgs||[]),s]}))}
                    className={"rounded-full border px-3 py-1 text-xs " + (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>{sdgLabel(s)}</button>;
                })}
              </div>
            </F>
            <div className="border-t border-neutral-100 pt-4">
              <RaisePurposePicker
                reasons={c.raise_reasons}
                setReasons={(v)=>setC({...c, raise_reasons: v})}
                other={c.raise_reason_other}
                setOther={(v)=>setC({...c, raise_reason_other: v})}
                quizPurposes={c.capital_purposes}
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-neutral-100 pt-4">
              <button onClick={onClose} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button>
              <button data-testid="edit-save" onClick={save} disabled={busy} className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  );
}
const inp = "w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]";
const F = ({ label, children }) => (<div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>{children}</div>);
